import { UseQueryResult } from '@tanstack/react-query'
import moment from 'moment'
import { createContext, useContext, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { CAAMetrics } from '@/modules/caa/modules/caaMetrics/domain/models/CAAMetrics'
import { useOperationalCAAMetricsPatientMonthQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/useOperationalCAAMetricsPatientMonthQuery'
import { useOperationalCAAMetricsPatientSemesterQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/useOperationalCAAMetricsPatientSemesterQuery'
import { useOperationalCAAMetricsPatientWeekQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/useOperationalCAAMetricsPatientWeekQuery'
import { useOperationalCAAMetricsPatientYearQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/useOperationalCAAMetricsPatientYearQuery'
import { useOperationalCAAMetricsTherapistMonthQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/useOperationalCAAMetricsTherapistMonthQuery'
import { useOperationalCAAMetricsTherapistSemesterQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/useOperationalCAAMetricsTherapistSemesterQuery'
import { useOperationalCAAMetricsTherapistWeekQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/useOperationalCAAMetricsTherapistWeekQuery'
import { useOperationalCAAMetricsTherapistYearQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/useOperationalCAAMetricsTherapistYearQuery'
import { usePatientCAAMetricsMonthQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/usePatientCAAMetricsMonthQuery'
import { usePatientCAAMetricsSemesterQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/usePatientCAAMetricsSemesterQuery'
import { usePatientCAAMetricsWeekQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/usePatientCAAMetricsWeekQuery'
import { usePatientCAAMetricsYearQuery } from '@/modules/caa/modules/caaMetrics/presentation/hooks/usePatientCAAMetricsYearQuery'

type PeriodType = 'WEEK' | 'MONTH' | 'SEMESTER' | 'YEAR'
type View = 'PATIENT' | 'OPERATIONAL-PATIENT' | 'OPERATIONAL-THERAPIST'

const filterInitialValue = {
  week: moment().startOf('week').format(moment.HTML5_FMT.DATE),
  month: moment().format(moment.HTML5_FMT.MONTH),
  semester: moment().get('month') > 5 ? '2' : '1',
  year: moment().year(),
  personaName: ''
} as const

const caaMetricsContext = createContext<{
  filter: {
    week: string
    month: string
    semester: '1' | '2'
    year: number
    personaName: string
  }
  rawInputs: {
    personaName: string
  }
  setWeek: React.Dispatch<React.SetStateAction<string>>
  setMonth: React.Dispatch<React.SetStateAction<string>>
  setSemester: React.Dispatch<React.SetStateAction<'1' | '2'>>
  setYear: React.Dispatch<React.SetStateAction<number>>
  setPersonaName: React.Dispatch<React.SetStateAction<string>>
  periodType: PeriodType
  setPeriodType: React.Dispatch<React.SetStateAction<PeriodType>>
  showPatient: boolean
  metricsQuery: UseQueryResult<CAAMetrics> | undefined
}>({
  filter: filterInitialValue,
  rawInputs: {
    personaName: ''
  },
  setWeek: () => {},
  setMonth: () => {},
  setSemester: () => {},
  setYear: () => {},
  setPersonaName: () => {},
  periodType: 'WEEK',
  setPeriodType: () => {},
  showPatient: false,
  metricsQuery: undefined
})

export const useCAAMetricsContext = () => useContext(caaMetricsContext)

export function CAAMetricsContextProvider({
  children,
  view,
  patientId
}: {
  children: React.ReactNode
  view: View
  patientId?: string
}) {
  const [week, setWeek] = useState(filterInitialValue.week)
  const [month, setMonth] = useState(filterInitialValue.month)
  const [semester, setSemester] = useState<'1' | '2'>(
    filterInitialValue.semester
  )
  const [year, setYear] = useState(filterInitialValue.year)
  const [personaName, setPersonaName] = useState<string>(
    filterInitialValue.personaName
  )
  const [debouncedPersonaName] = useDebounceValue(personaName, 500)

  const [periodType, setPeriodType] = useState<PeriodType>('WEEK')

  const patientCAAMetricsWeekQuery = usePatientCAAMetricsWeekQuery({
    date: week,
    patientId: patientId ?? '',
    enabled: periodType === 'WEEK' && view === 'PATIENT',
    personaName: debouncedPersonaName
  })
  const patientCAAMetricsMonthQuery = usePatientCAAMetricsMonthQuery({
    month: moment(month).month() + 1,
    year: moment(month).year(),
    patientId: patientId ?? '',
    enabled:
      periodType === 'MONTH' && view === 'PATIENT' && moment(month).isValid(),
    personaName: debouncedPersonaName
  })
  const patientCAAMetricsSemesterQuery = usePatientCAAMetricsSemesterQuery({
    period: +semester,
    year,
    patientId: patientId ?? '',
    enabled: periodType === 'SEMESTER' && view === 'PATIENT',
    personaName: debouncedPersonaName
  })
  const patientCAAMetricsYearQuery = usePatientCAAMetricsYearQuery({
    year,
    patientId: patientId ?? '',
    enabled: periodType === 'YEAR' && view === 'PATIENT',
    personaName: debouncedPersonaName
  })
  const operationalCAAMetricsPatientWeekQuery =
    useOperationalCAAMetricsPatientWeekQuery({
      date: week,
      enabled: periodType === 'WEEK' && view === 'OPERATIONAL-PATIENT',
      personaName: debouncedPersonaName
    })
  const operationalCAAMetricsPatientMonthQuery =
    useOperationalCAAMetricsPatientMonthQuery({
      month: moment(month).month() + 1,
      year: moment(month).year(),
      enabled: periodType === 'MONTH' && view === 'OPERATIONAL-PATIENT',
      personaName: debouncedPersonaName
    })
  const operationalCAAMetricsPatientSemesterQuery =
    useOperationalCAAMetricsPatientSemesterQuery({
      period: +semester,
      year,
      enabled: periodType === 'SEMESTER' && view === 'OPERATIONAL-PATIENT',
      personaName: debouncedPersonaName
    })
  const operationalCAAMetricsPatientYearQuery =
    useOperationalCAAMetricsPatientYearQuery({
      year,
      enabled: periodType === 'YEAR' && view === 'OPERATIONAL-PATIENT',
      personaName: debouncedPersonaName
    })
  const operationalCAAMetricsTherapistWeekQuery =
    useOperationalCAAMetricsTherapistWeekQuery({
      date: week,
      enabled: periodType === 'WEEK' && view === 'OPERATIONAL-THERAPIST',
      personaName: debouncedPersonaName
    })
  const operationalCAAMetricsTherapistMonthQuery =
    useOperationalCAAMetricsTherapistMonthQuery({
      month: moment(month).month() + 1,
      year: moment(month).year(),
      enabled: periodType === 'MONTH' && view === 'OPERATIONAL-THERAPIST',
      personaName: debouncedPersonaName
    })
  const operationalCAAMetricsTherapistSemesterQuery =
    useOperationalCAAMetricsTherapistSemesterQuery({
      period: +semester,
      year,
      enabled: periodType === 'SEMESTER' && view === 'OPERATIONAL-THERAPIST',
      personaName: debouncedPersonaName
    })
  const operationalCAAMetricsTherapistYearQuery =
    useOperationalCAAMetricsTherapistYearQuery({
      year,
      enabled: periodType === 'YEAR' && view === 'OPERATIONAL-THERAPIST',
      personaName: debouncedPersonaName
    })

  const metricsQuery = {
    PATIENT: {
      WEEK: patientCAAMetricsWeekQuery,
      MONTH: patientCAAMetricsMonthQuery,
      SEMESTER: patientCAAMetricsSemesterQuery,
      YEAR: patientCAAMetricsYearQuery
    },
    'OPERATIONAL-PATIENT': {
      WEEK: operationalCAAMetricsPatientWeekQuery,
      MONTH: operationalCAAMetricsPatientMonthQuery,
      SEMESTER: operationalCAAMetricsPatientSemesterQuery,
      YEAR: operationalCAAMetricsPatientYearQuery
    },
    'OPERATIONAL-THERAPIST': {
      WEEK: operationalCAAMetricsTherapistWeekQuery,
      MONTH: operationalCAAMetricsTherapistMonthQuery,
      SEMESTER: operationalCAAMetricsTherapistSemesterQuery,
      YEAR: operationalCAAMetricsTherapistYearQuery
    }
  }[view][periodType]

  const showPatient = view === 'OPERATIONAL-PATIENT'

  return (
    <caaMetricsContext.Provider
      value={{
        filter: {
          week,
          month,
          semester,
          year,
          personaName
        },
        rawInputs: {
          personaName: debouncedPersonaName
        },
        setWeek,
        setMonth,
        setSemester,
        setYear,
        setPersonaName,
        periodType,
        setPeriodType,
        showPatient,
        metricsQuery
      }}
    >
      {children}
    </caaMetricsContext.Provider>
  )
}
